<!-- // ============================================
// 8. AuctionHistory.vue
// ============================================ -->
<template>
    <div class="info-block" v-if="bids">
        <div v-if="bids.length > 0" class="mb-2">
            <button @click="showHistory = !showHistory" type="button" class="btn btn-default bid-history-link">
                [{{ bids.length }} {{ $t('ecommerce.bids') }}]
            </button>
        </div>
        
        <div v-if="showHistory" class="auction-history-header">
            <h3>{{ $t('ecommerce.auction-history') }}</h3>
            <button v-if="bids.length > 0" class="btn-refresh" :disabled="loading">
                <span class="mdi mdi-refresh" :class="{ 'rotating': loading }"></span>
            </button>
        </div>

        <div v-if="showHistory" class="auction-history-content">
            <LoadingSpinner v-if="loading" />

            <div v-else-if="bids.length > 0" class="bids-list">
                <div v-for="bid in bids" :key="bid.id" class="bid-item">
                    <div class="bid-header">
                        <span class="bid-price">{{ formatPrice(bid.price) }}</span>
                    </div>
                    <div class="bid-details">
                        <div class="bid-user">
                            <strong>{{ maskEmail(bid.user.email) }}</strong>
                        </div>
                        <div class="bid-date">
                            <span class="mdi mdi-clock-outline"></span>
                            {{ bid.created_at | moment }}
                        </div>
                    </div>
                </div>
            </div>

            <div v-else class="empty-state">
                <span class="mdi mdi-gavel"></span>
                <p>{{ $t('sentence.noBidsYet') }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import LoadingSpinner from "@/components/templates/LoadingSpinner.vue";
import helper from "@/services/helper";

export default {
    name: 'AuctionHistory',
    components: {
        LoadingSpinner
    },
    props: {
        bids: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            showHistory: false
        };
    },
    methods: {
        formatPrice: helper.formatPrice,
        maskEmail: helper.maskEmail,
    }
};
</script>

/* ============================================
   8. AuctionHistory.vue - Component Specific Styles
   Import shared-components.css for common styles
   ============================================ */
<style scoped>
/* Auction History Header - Unique to AuctionHistory */
.auction-history-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.auction-history-header h3 {
    margin: 0;
}

.btn-refresh {
    background: none;
    border: 1px solid var(--gray-200);
    border-radius: 6px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    color: var(--gray-500);
}

.btn-refresh:hover:not(:disabled) {
    background: var(--gray-100);
    border-color: var(--gray-300);
    color: #1f2937;
}

.btn-refresh:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-refresh .mdi {
    font-size: 1.25rem;
}

.rotating {
    animation: rotate 1s linear infinite;
}

@keyframes rotate {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

/* Auction Content - Unique to AuctionHistory */
.auction-history-content {
    min-height: 100px;
}

.bids-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.bid-item {
    background: var(--gray-50);
    border: 1px solid var(--gray-200);
    border-radius: 8px;
    padding: 14px;
    transition: all 0.2s;
}

.bid-item:hover {
    background: var(--gray-100);
    border-color: var(--gray-300);
}

.bid-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.bid-price {
    flex: 1;
}

.bid-details {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

.bid-user,
.bid-date {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.875rem;
    color: var(--gray-500);
}

.bid-user .mdi,
.bid-date .mdi {
    font-size: 1rem;
}

@media (max-width: 640px) {
    .bid-details {
        align-items: flex-start;
        gap: 8px;
    }

    .auction-history-header {
        padding: 16px 20px;
    }

    .auction-history-content {
        padding: 16px 20px;
    }
}
</style>